import { getErrorResponseMessage } from "@via/utils/helpers.tsx";
import { type FormEvent, type JSX, useState } from "react";
import {
  useRevokeTrustedRepository,
  useTrustedRepositories,
  useTrustRepository,
} from "./trusted-repositories.api.tsx";

const TrustedRepositories = (): JSX.Element => {
  const { data, isLoading, isError, error } = useTrustedRepositories();
  const trust = useTrustRepository();
  const revoke = useRevokeTrustedRepository();
  const [repoUrl, setRepoUrl] = useState("");
  const [note, setNote] = useState("");

  const submit = (event: FormEvent) => {
    event.preventDefault();
    trust.mutate(
      { repoUrl, note },
      {
        onSuccess: () => {
          setRepoUrl("");
          setNote("");
        },
      }
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-gray-700">
        Code imported from git runs only when its repository is trusted here.
        Trusting a repository trusts everyone who can push to it, on every
        branch and tag. Revoking stops new runs at once; runs already going are
        not stopped.
      </p>
      <form onSubmit={submit} className="flex flex-col gap-3 max-w-xl">
        <label className="flex flex-col text-sm">
          Repository address (https)
          <input
            className="border rounded px-2 py-1"
            value={repoUrl}
            onChange={e => setRepoUrl(e.target.value)}
            required
          />
        </label>
        <label className="flex flex-col text-sm">
          Why it is trusted
          <input
            className="border rounded px-2 py-1"
            value={note}
            onChange={e => setNote(e.target.value)}
            maxLength={500}
            required
          />
        </label>
        <button
          type="submit"
          className="self-start rounded bg-blue-600 px-3 py-1 text-white"
          disabled={trust.isPending}>
          Trust repository
        </button>
      </form>
      {isLoading ? (
        <p>Loading…</p>
      ) : isError ? (
        // A failed load must never read as an empty list, which would claim nothing is trusted.
        <div role="alert" className="text-sm text-red-700">
          <p>
            The trusted repository list could not be loaded, so this page cannot
            show which repositories are trusted. Reload the page to try again.
          </p>
          {getErrorResponseMessage(error) && (
            <p>{getErrorResponseMessage(error)}</p>
          )}
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left">
              <th>Repository</th>
              <th>Why</th>
              <th>Trusted by</th>
              <th>Trusted</th>
              <th>Processes unblocked</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(data?.data ?? []).map(row => (
              <tr key={row.id} className={row.revokedAt ? "text-gray-400" : ""}>
                <td>{row.repoUrl}</td>
                <td>{row.note}</td>
                <td>{row.trustedByEmail ?? "unknown"}</td>
                <td>{new Date(row.trustedAt).toLocaleString()}</td>
                <td>{row.unblockedProcessCount}</td>
                <td>
                  {row.revokedAt ? (
                    "Revoked"
                  ) : (
                    <button
                      type="button"
                      className="text-red-600"
                      disabled={revoke.isPending}
                      onClick={() => revoke.mutate(row.id)}>
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default TrustedRepositories;
